import { Separator as SeparatorPrimitive } from 'radix-ui';
import { type ComponentProps } from 'react';

import { cn } from './utils/cn';

/**
 * Props for the {@link Separator} component.
 *
 * Extends the props of the primitive so `ref`, `data-*`, `aria-*`,
 * and `style` pass through to the underlying element.
 */
export type SeparatorProps = ComponentProps<typeof SeparatorPrimitive.Root> & {
  /**
   * Axis of the separator. Horizontal renders as a 1px-tall, full-width line;
   * vertical renders as a 1px-wide line that stretches to its parent's height.
   * @default 'horizontal'
   */
  orientation?: ComponentProps<typeof SeparatorPrimitive.Root>['orientation'];
  /**
   * When true, the separator is treated as purely visual and given no
   * accessible role. Set to `false` if it semantically separates related
   * groups of content (it will then expose `role="separator"`).
   * @default true
   */
  decorative?: boolean;
};

/**
 * Visual rule that separates content. The canonical horizontal/vertical divider.
 *
 * Orientation styling is driven by `data-orientation`, so callers can
 * override layout via `className` (e.g. `flex-1` to share space inside a
 * flex row) without fighting baked-in dimensions. The component ships with
 * no built-in margin: add `my-*` / `mx-*` explicitly when extra spacing is wanted.
 *
 * @example
 * // Horizontal rule with added vertical spacing
 * <Separator className="my-2" />
 *
 * @example
 * // Vertical rule inside a horizontal flex row
 * <Stack direction="horizontal" align="center" gap={2}>
 *   <span>Left</span>
 *   <Separator orientation="vertical" />
 *   <span>Right</span>
 * </Stack>
 */
function Separator({
  className,
  orientation = 'horizontal',
  decorative = true,
  ...props
}: SeparatorProps) {
  return (
    <SeparatorPrimitive.Root
      data-slot="separator"
      decorative={decorative}
      orientation={orientation}
      className={cn(
        'shrink-0 bg-border data-[orientation=horizontal]:h-px data-[orientation=horizontal]:w-full data-[orientation=vertical]:w-px data-[orientation=vertical]:self-stretch',
        className,
      )}
      {...props}
    />
  );
}

export { Separator };
